import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  Box,
  Button,
  TextField,
  Typography,
  Avatar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

// Base URL for API (for debugging)
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:4000' 
  : '';

export const ItemSelector = ({ open, onClose, onSelect }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    cost: '',
    description: '',
    instruction: ''
  });

  const fetchItems = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/items`);
      const data = await response.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchItems();
    }
  }, [open]);

  const handleCreateSubmit = async () => {
    try {
      if (!formData.name) {
        throw new Error('Name is required');
      }

      const response = await fetch(`${API_BASE_URL}/api/items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save item');
      }

      const newItem = await response.json();
      setCreateDialogOpen(false);
      setFormData({ name: '', cost: '', description: '', instruction: '' });
      await fetchItems();
      
      onSelect({ 
        id: newItem._id, 
        name: newItem.name,
        cost: newItem.cost,
        description: newItem.description
      });
    } catch (error) {
      console.error('Error saving item:', error);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Select Item
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setCreateDialogOpen(true)}
              startIcon={<AddIcon />}
            >
              Add Item
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ padding: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ padding: 1 }}>Icon</TableCell>
                    <TableCell sx={{ padding: 1 }}>Name</TableCell>
                    <TableCell sx={{ padding: 1 }}>Cost</TableCell>
                    <TableCell sx={{ padding: 1 }}>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow 
                      key={item._id}
                      sx={{ 
                        '&:hover': { bgcolor: 'action.hover' },
                        cursor: 'pointer'
                      }}
                      onClick={() => onSelect({ 
                        id: item._id, 
                        name: item.name,
                        cost: item.cost,
                        description: item.description
                      })}
                    >
                      <TableCell sx={{ padding: 1 }}>
                        {item.icon ? (
                          <Avatar 
                            src={item.icon} 
                            alt={item.name}
                            sx={{ width: 32, height: 32 }}
                          />
                        ) : (
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {item.name.charAt(0)}
                          </Avatar>
                        )}
                      </TableCell>
                      <TableCell sx={{ padding: 1 }}>
                        <Chip color="primary" label={item.name} />
                      </TableCell>
                      <TableCell sx={{ padding: 1 }}>
                        <Typography variant="body2" color="primary" fontFamily="monospace">
                          {item.cost}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ padding: 1 }}>{item.description}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create item</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label="Cost"
              type="number"
              value={formData.cost}
              onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Instruction"
              value={formData.instruction}
              onChange={(e) => setFormData({ ...formData, instruction: e.target.value })}
              fullWidth
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateSubmit} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}; 